import React, { useEffect, useState, useCallback } from 'react';
import { CheckCircle, AlertCircle, AlertTriangle, Info, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';

/**
 * Enterprise Toast Component
 * Supports success, error, warning, info types with optional action buttons
 */

const TOAST_COLORS = {
  success: {
    bg: 'rgba(16, 185, 129, 0.95)',
    border: '#10B981',
    text: '#FFFFFF',
    icon: CheckCircle
  },
  error: {
    bg: 'rgba(239, 68, 68, 0.95)',
    border: '#EF4444',
    text: '#FFFFFF',
    icon: AlertCircle
  },
  warning: {
    bg: 'rgba(245, 158, 11, 0.95)',
    border: '#F59E0B',
    text: '#FFFFFF',
    icon: AlertTriangle
  },
  info: {
    bg: 'rgba(59, 130, 246, 0.95)',
    border: '#3B82F6',
    text: '#FFFFFF',
    icon: Info
  }
};

export default function Toast({
  id,
  message,
  type = 'success',
  onClose,
  duration = 3000,
  action = null // { label: string, onClick: () => void }
}) {
  const { t } = useTranslation();
  const [isExiting, setIsExiting] = useState(false);
  const [progress, setProgress] = useState(100);

  const handleClose = useCallback(() => {
    setIsExiting(true);
    setTimeout(() => {
      onClose(id);
    }, 200); // Match exit animation duration
  }, [id, onClose]);

  // Auto-dismiss timer with progress bar
  useEffect(() => {
    if (duration <= 0) return;

    const startTime = Date.now();
    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, 100 - (elapsed / duration) * 100);
      setProgress(remaining);

      if (remaining <= 0) {
        clearInterval(interval);
        handleClose();
      }
    }, 50);

    return () => clearInterval(interval);
  }, [duration, handleClose]);

  // Get colors for current type
  const colors = TOAST_COLORS[type] || TOAST_COLORS.info;
  const IconComponent = colors.icon;

  return (
    <div
      style={{
        backgroundColor: colors.bg,
        borderRadius: '0.75rem',
        padding: '0',
        marginBottom: '0.75rem',
        boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
        animation: isExiting ? 'toastSlideOut 0.2s ease-in forwards' : 'toastSlideIn 0.3s ease-out',
        color: colors.text,
        fontSize: '0.95rem',
        maxWidth: '420px',
        minWidth: '300px',
        overflow: 'hidden',
        position: 'relative'
      }}
    >
      {/* Main content */}
      <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '0.75rem',
        padding: '1rem'
      }}>
        {/* Icon */}
        <div style={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          color: colors.text,
          marginTop: '2px'
        }}>
          <IconComponent size={20} />
        </div>

        {/* Message and Action */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            wordBreak: 'break-word',
            lineHeight: '1.5'
          }}>
            {message}
          </div>

          {/* Action button (e.g., Undo) */}
          {action && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                action.onClick();
                handleClose();
              }}
              style={{
                background: 'rgba(255,255,255,0.2)',
                border: 'none',
                padding: '0.375rem 0.75rem',
                borderRadius: '0.375rem',
                cursor: 'pointer',
                color: colors.text,
                fontSize: '0.85rem',
                fontWeight: '600',
                marginTop: '0.5rem',
                transition: 'background 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.3)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}
            >
              {action.label}
            </button>
          )}
        </div>

        {/* Close button */}
        <button
          onClick={handleClose}
          style={{
            background: 'none',
            border: 'none',
            padding: '0.25rem',
            cursor: 'pointer',
            color: colors.text,
            display: 'flex',
            alignItems: 'center',
            opacity: 0.7,
            transition: 'opacity 0.2s',
            flexShrink: 0
          }}
          onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
          onMouseLeave={(e) => e.currentTarget.style.opacity = '0.7'}
          aria-label={t('common.closeNotification', 'Close notification')}
        >
          <X size={18} />
        </button>
      </div>

      {/* Progress bar */}
      {duration > 0 && (
        <div style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '3px',
          backgroundColor: 'rgba(255,255,255,0.2)'
        }}>
          <div style={{
            height: '100%',
            width: `${progress}%`,
            backgroundColor: 'rgba(255,255,255,0.5)',
            transition: 'width 0.05s linear'
          }} />
        </div>
      )}

      <style>{`
        @keyframes toastSlideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        @keyframes toastSlideOut {
          from {
            transform: translateX(0);
            opacity: 1;
          }
          to {
            transform: translateX(100%);
            opacity: 0;
          }
        }
      `}</style>
    </div>
  );
}

/**
 * Simple toast creation helpers for use with toast context
 */
export const createToast = {
  success: (message, options = {}) => ({ message, type: 'success', ...options }),
  error: (message, options = {}) => ({ message, type: 'error', duration: 5000, ...options }),
  warning: (message, options = {}) => ({ message, type: 'warning', duration: 4000, ...options }),
  info: (message, options = {}) => ({ message, type: 'info', ...options }),

  // Special toast with undo action
  withUndo: (message, onUndo, options = {}) => ({
    message,
    type: 'success',
    duration: 5000,
    action: { label: i18next.t('common.undo') || 'Undo', onClick: onUndo },
    ...options
  })
};
